import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { clubPost } from "./clubApi";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { canEdit } from "../../include/staffAccess";

export default function ClubStores() {
  const { clubData } = useContext(DataContext);
  const clubId = clubData?.club_info?.club_id;
  const [stores, setStores] = useState([]);
  const [remove, setRemove] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!clubId) return;
    clubPost("CLUB_STORES", { club_id: clubId }).then((result) => {
      if (result.status === "200") setStores(result.data?.stores || []);
    });
  }, [clubId]);

  useEffect(() => { load(); }, [load]);

  const confirmRemove = async () => {
    if (!remove) return;
    setSaving(true);
    try {
      const result = await clubPost("CLUB_STORE_REMOVE", { club_id: clubId, store_id: remove.store_id });
      if (result.status === "200") {
        toast.success(result.data?.message || "Store removed");
        setRemove(null);
        load();
      } else toast.info(result.status_message || result.data?.error || "Could not remove store");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography sx={{ color: consoleColors.muted, fontSize: 14, mb: 2 }}>Stores that belong to this club. Coupons issued are Lucky Draw Coupons from this club.</Typography>
      {stores.length === 0 && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>
          No stores in this club yet.
        </Box>
      )}
      <Box sx={{ display: "grid", gap: 1.5 }}>
        {stores.map((store) => (
          <Box key={store.store_id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{store.store_name}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>
                {[store.city_name, store.owner_name, store.contact_mobile, store.status === "1" ? "Enabled" : "Disabled"].filter(Boolean).join(" · ")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.1 }}>
                <Box sx={{ background: "#f8fafc", border: `1px solid ${consoleColors.line}`, borderRadius: 1.5, px: 1.2, py: 0.7 }}>
                  <Typography sx={{ fontSize: 11, color: consoleColors.muted, fontWeight: 700 }}>Coupons issued</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 750, lineHeight: 1.2 }}>{store.coupon_issued || 0}</Typography>
                </Box>
                <Box sx={{ background: "#f8fafc", border: `1px solid ${consoleColors.line}`, borderRadius: 1.5, px: 1.2, py: 0.7 }}>
                  <Typography sx={{ fontSize: 11, color: consoleColors.muted, fontWeight: 700 }}>Spin count</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 750, lineHeight: 1.2 }}>{store.spin_count || 0}</Typography>
                </Box>
              </Box>
            </Box>
            {canEdit(clubData) && (
              <Button size="small" color="error" sx={{ textTransform: "none" }} onClick={() => setRemove(store)}>Remove from club</Button>
            )}
          </Box>
        ))}
      </Box>

      <Dialog open={Boolean(remove)} onClose={() => setRemove(null)}>
        <DialogTitle>Remove store</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove {remove?.store_name || "this store"} from the club? Issued coupons and spins stay in that store. It will no longer use this club’s Lucky Draw Coupons.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button sx={{ textTransform: "none" }} onClick={() => setRemove(null)}>Cancel</Button>
          <Button variant="contained" color="error" sx={{ ...primaryButtonSx, background: "#b91c1c" }} disabled={saving} onClick={confirmRemove}>
            {saving ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
